import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import {
  X,
  Send,
  Loader2,
  Lightbulb,
  CheckCircle,
  Calendar,
  FileText,
  Zap
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import LouisAvatar from "./LouisAvatar";
import { useNavigate } from "react-router-dom";

const suggestions = [
  "Crée un post Facebook sur la rentrée",
  "Planifie un post pour demain",
  "Montre les posts en attente",
  "Approuve tous mes posts"
];

const ActionBadge = ({ action }) => {
  const badges = {
    create_post: { icon: FileText, label: "Post créé", color: "bg-green-500" },
    create_event: { icon: Calendar, label: "Événement planifié", color: "bg-purple-500" },
    approve_all: { icon: CheckCircle, label: "Posts approuvés", color: "bg-blue-500" },
    publish_post: { icon: Zap, label: "Post publié", color: "bg-orange-500" },
    list_pending: { icon: FileText, label: "Liste", color: "bg-gray-500" },
    list_approved: { icon: CheckCircle, label: "Liste", color: "bg-gray-500" },
  };
  
  const badge = badges[action];
  if (!badge) return null;
  
  const Icon = badge.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs text-white ${badge.color}`}>
      <Icon className="w-3 h-3" />
      {badge.label}
    </span>
  );
};

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Salut ! Je suis Louis, ton assistant CLYDE 👋\n\nJe peux créer des posts, les planifier et les publier pour toi. Dis-moi ce que tu veux faire !",
      action: null
    }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage = { role: "user", content: messageText.trim() };
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: messageText.trim(),
        conversation_history: messages.slice(-10)
      }, { timeout: 90000 });

      if (response.data.success) {
        const actionResult = response.data.action_result;
        setMessages(prev => [...prev, {
          role: "assistant",
          content: response.data.response || actionResult?.message || "Action effectuée !",
          action: actionResult
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: response.data.response || "Désolé, j'ai rencontré un problème."
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "Oups ! J'ai eu un petit souci. Réessaie dans quelques secondes 😅"
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button avec Louis */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-20 right-6 z-50 w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 ${
          isOpen 
            ? "bg-gray-200" 
            : "bg-white border-2 border-primary"
        }`}
        style={{
          boxShadow: isOpen ? '0 4px 15px rgba(0,0,0,0.1)' : '0 4px 20px rgba(249, 115, 22, 0.3)'
        }}
        data-testid="chatbot-toggle"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-gray-600" />
        ) : (
          <LouisAvatar size={52} />
        )}
      </button>

      {/* Bulle "Besoin d'aide ?" */}
      {!isOpen && (
        <div className="fixed bottom-24 right-24 z-40 bg-white px-4 py-2 rounded-full shadow-lg border border-border animate-bounce-slow">
          <p className="text-sm font-medium text-foreground">Demande-moi ! 🚀</p>
          <div className="absolute -right-2 top-1/2 -translate-y-1/2 w-0 h-0 border-t-8 border-b-8 border-l-8 border-transparent border-l-white"></div>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-36 right-6 z-50 w-[420px] h-[550px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-slide-up"
          data-testid="chatbot-window"
        >
          {/* Header avec Louis */}
          <div className="p-4 border-b border-border bg-gradient-to-r from-primary/10 to-orange-100">
            <div className="flex items-center gap-3">
              <div className="relative">
                <LouisAvatar size={48} isThinking={isLoading} />
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
              </div>
              <div>
                <h3 className="font-bold text-sm">Louis</h3>
                <p className="text-xs text-muted-foreground">
                  {isLoading ? "Je travaille dessus... ⚡" : "Assistant CLYDE"}
                </p>
              </div>
              <div className="ml-auto flex gap-1">
                <span className="px-2 py-1 bg-primary/10 rounded-full text-xs text-primary font-medium">
                  IA Active
                </span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${message.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div className="flex-shrink-0">
                    {message.role === "user" ? (
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-bold text-primary">Moi</span>
                      </div>
                    ) : (
                      <LouisAvatar size={32} />
                    )}
                  </div>
                  <div className={`max-w-[80%] space-y-2`}>
                    {/* Action badge if present */}
                    {message.action?.success && (
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600 font-medium">Action réussie</span>
                      </div>
                    )}
                    
                    <div className={`p-3 rounded-2xl text-sm ${
                      message.role === "user"
                        ? "bg-primary text-white rounded-tr-sm"
                        : "bg-muted rounded-tl-sm"
                    }`}>
                      <p className="whitespace-pre-wrap">{message.content}</p>
                    </div>
                    
                    {/* Show created posts preview */}
                    {message.action?.posts && message.action.posts.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {message.action.posts.slice(0, 2).map((post, idx) => (
                          <div key={idx} className="p-2 bg-white rounded-lg border border-border text-xs">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`px-1.5 py-0.5 rounded text-white text-[10px] ${
                                post.network === 'facebook' ? 'bg-[#1877F2]' :
                                post.network === 'instagram' ? 'bg-[#E4405F]' : 'bg-[#0A66C2]'
                              }`}>
                                {post.network?.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-muted-foreground line-clamp-2">{post.preview || post.content?.substring(0, 80)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              
              {isLoading && (
                <div className="flex gap-3">
                  <LouisAvatar size={32} isThinking={true} />
                  <div className="bg-muted p-3 rounded-2xl rounded-tl-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Louis travaille...</span>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Quick Actions */}
          {messages.length <= 2 && !isLoading && (
            <div className="px-4 pb-2">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                <Zap className="w-3 h-3 text-primary" />
                Actions rapides
              </p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSend(suggestion)}
                    className="text-xs px-3 py-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors text-left border border-primary/20"
                    disabled={isLoading}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-border bg-card">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ex: Crée un post sur les vacances..."
                className="flex-1"
                disabled={isLoading}
                data-testid="chatbot-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!input.trim() || isLoading}
                className="bg-primary hover:bg-primary/90"
                data-testid="chatbot-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 text-center">
              Louis peut créer, planifier et publier tes posts
            </p>
          </div>
        </div>
      )}

      {/* Styles pour l'animation bounce lente */}
      <style>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
      `}</style>
    </>
  );
}
